import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { VictoryChart, VictoryAxis, VictoryLine, VictoryLabel, VictoryTooltip } from 'victory';

import { ChartContainer, NoDataContainer } from './LineBarChart.styles';

import {
    legendOptions,
    getLightningBar,
    getWindLine,
    getWindAxis,
    getBottomAxis,
    getLegend,
    keyToTZ,
} from './LineBarChart';
import { chartTheme, colorLightBlue } from '../../common/ChartTheme';
import { getDomainForDay } from '../Timeline/Timeline.helpers';
import { NoData } from '../NoData/NoData';
import { defaultVictoryVoronoiContainer } from '../_charts/Chart.defaults';
import { roundTo5 } from 'common/numbers';
import { useRematchSelector } from 'models/rematchStore';

const selectedLegendOptions = [
    legendOptions.observedLightning,
    legendOptions.windSpeed,
    legendOptions.forecastWaveHeight,
];

const FORECAST_WAVE_HEIGHT_DEFAULT_MAXIMUM = 5;
const WIND_SPEED_DEFAULT_MAXIMUM = 20;

export const WindAndWaveChart = ({ chartWidth, chartHeight }) => {
    const {
        totalGenerationChart: { windSpeedData = [], observedLightningData = [], forecastWaveHeightData = [] },
    } = useRematchSelector((state) => state);
    const forecastWaveHeightMaximum = Math.max(
        ...forecastWaveHeightData.map((item) => roundTo5(item.value)),
        FORECAST_WAVE_HEIGHT_DEFAULT_MAXIMUM
    );
    const windSpeedMaximum = Math.max(...windSpeedData.map((item) => roundTo5(item.value)), WIND_SPEED_DEFAULT_MAXIMUM);

    const internalChartDay = windSpeedData[0] ? moment.utc(windSpeedData[0].key) : moment();

    const thereIsNoData =
        windSpeedData.length === 0 && observedLightningData.length === 0 && forecastWaveHeightData.length === 0;

    const chartPadding = { left: 40, right: 40, top: 40, bottom: 70 };
    const chartOffsetX = (chartWidth - chartPadding.left - chartPadding.right) / 48;

    return (
        <>
            <ChartContainer>
                <VictoryChart
                    domainPadding={{
                        x: [chartOffsetX, -chartOffsetX],
                    }}
                    width={chartWidth}
                    theme={chartTheme}
                    padding={chartPadding}
                    domain={{
                        x: getDomainForDay(internalChartDay),
                        y: [0, 1],
                    }}
                    scale={{ x: 'time', y: 'linear' }}
                    containerComponent={defaultVictoryVoronoiContainer}>
                    {getLegend(selectedLegendOptions, chartWidth, chartHeight)}

                    {getWindAxis(undefined, windSpeedMaximum)}
                    {getBottomAxis(chartWidth, chartOffsetX, internalChartDay)}
                    <VictoryAxis
                        key="forecast-wave-height-axis"
                        name="forecast-wave-height-axis"
                        orientation="right"
                        axisLabelComponent={<VictoryLabel verticalAnchor="start" y={0} x={chartWidth - 74} angle={0} />}
                        label="Forecast Wave Height [m]"
                        dependentAxis
                        tickFormat={(t) => t * forecastWaveHeightMaximum}
                    />
                    {forecastWaveHeightData.length > 0 && (
                        <VictoryLine
                            key="forecast-wave-height-chart"
                            name="forecast-wave-height-chart"
                            style={{
                                data: {
                                    stroke: colorLightBlue,
                                    strokeWidth: 3,
                                },
                            }}
                            y={(d) => {
                                const scaledValue = d.value / forecastWaveHeightMaximum;
                                return d.value === null ? null : scaledValue;
                            }}
                            x={keyToTZ}
                            labels={({ datum }) =>
                                datum.value === null ? 'Data Unavailable' : `${Math.round(datum.value * 100) / 100} m`
                            }
                            labelComponent={<VictoryTooltip {...chartTheme.tooltip} />}
                            data={forecastWaveHeightData}
                        />
                    )}

                    {getWindLine(windSpeedData, windSpeedMaximum)}
                    {getLightningBar(observedLightningData, chartWidth, chartPadding)}
                </VictoryChart>
            </ChartContainer>
            {thereIsNoData && (
                <NoDataContainer>
                    <NoData />
                </NoDataContainer>
            )}
        </>
    );
};

WindAndWaveChart.displayName = 'WindAndWaveChart';

WindAndWaveChart.propTypes = {
    chartWidth: PropTypes.number,
    chartHeight: PropTypes.number,
};

WindAndWaveChart.defaultProps = {
    chartWidth: 920,
    chartHeight: 240,
};
